/* The badge is positioned at the edge midpoint computed by React Flow, which must be applied inline. */
/* eslint-disable react/forbid-dom-props */
import {
	BaseEdge,
	EdgeLabelRenderer,
	type EdgeProps,
	type Edge as FlowEdge,
	getStraightPath,
	useInternalNode
} from "@xyflow/react";
import React from "react";

import { getFloatingEdgeParams } from "./floatingEdge.utils";
import type { RelationBadgeKind } from "./treeGraph.types";

import styles from "./RelationBadgeEdge.module.css";

/** Midpoint glyph per relation kind: rings, heart, companions, siblings, crossed swords, bolt, paw, banner. */
const badgeIcon = (kind: RelationBadgeKind): React.ReactNode => {
	switch (kind) {
		case "marriage":
			return (
				<>
					<circle cx="9" cy="12" r="5" />
					<circle cx="15" cy="12" r="5" />
				</>
			);
		case "love":
			return <path d="M12 19.2 5.7 12.9a3.7 3.7 0 0 1 5.2-5.2l1.1 1 1.1-1a3.7 3.7 0 0 1 5.2 5.2Z" />;
		case "friend":
			return (
				<>
					<circle cx="8.5" cy="9" r="3" />
					<circle cx="15.5" cy="9" r="3" />
					<path d="M4 19c.6-3.4 2.4-5.2 4.5-5.2S12.4 15.6 13 19" />
					<path d="M11 19c.6-3.4 2.4-5.2 4.5-5.2S19.4 15.6 20 19" />
				</>
			);
		case "sibling":
			return (
				<>
					<path d="M8 4h8" />
					<path d="M8 4v3" />
					<path d="M16 4v3" />
					<circle cx="8" cy="9.8" r="2.5" />
					<circle cx="16" cy="9.8" r="2.5" />
					<path d="M4.8 19.5c.5-3.2 1.8-4.9 3.2-4.9s2.7 1.7 3.2 4.9" />
					<path d="M12.8 19.5c.5-3.2 1.8-4.9 3.2-4.9s2.7 1.7 3.2 4.9" />
				</>
			);
		case "enemy":
			return (
				<>
					<path d="M5.5 5.5 18 18" />
					<path d="M18.5 5.5 6 18" />
					<path d="m14.6 18.4 3.8-3.8" />
					<path d="M9.4 18.4 5.6 14.6" />
				</>
			);
		case "rival":
			return <path d="M13 3 6 13.5h4.6L9 21l8.5-11h-4.8L14 3Z" />;
		case "succession":
			return (
				<>
					<path d="M18 8.5A7 7 0 0 0 6.2 7.6" />
					<path d="M6.5 3.5v4.3h4.3" />
					<path d="M6 15.5a7 7 0 0 0 11.8.9" />
					<path d="M17.5 20.5v-4.3h-4.3" />
				</>
			);
		case "bond":
			return (
				<>
					<ellipse cx="12" cy="15.5" rx="4.2" ry="3.4" />
					<circle cx="6.4" cy="10.6" r="1.6" />
					<circle cx="10.2" cy="7.8" r="1.6" />
					<circle cx="13.8" cy="7.8" r="1.6" />
					<circle cx="17.6" cy="10.6" r="1.6" />
				</>
			);
		case "team":
		default:
			return (
				<>
					<path d="M7 4v17" />
					<path d="M7 5h10l-3 3.5 3 3.5H7" />
				</>
			);
	}
};

/** `derived` marks a connector the canvas inferred (shared-parent siblings); the canvas gates its visibility. */
type RelationBadgeEdgeData = { kind: RelationBadgeKind; tooltip?: string; derived?: boolean };

/** Floating connector with a meaning icon at its midpoint (marriage rings, crossed swords, team banner, ...). */
const RelationBadgeEdge = ({ data, id, source, target }: EdgeProps<FlowEdge<RelationBadgeEdgeData>>) => {
	const sourceNode = useInternalNode(source);
	const targetNode = useInternalNode(target);

	if (!sourceNode || !targetNode) {
		return null;
	}

	const kind: RelationBadgeKind = data?.kind ?? "team";
	const { sx, sy, tx, ty } = getFloatingEdgeParams(sourceNode, targetNode);
	const [path, labelX, labelY] = getStraightPath({ sourceX: sx, sourceY: sy, targetX: tx, targetY: ty });

	const lineClassName = kind === "enemy" ? styles.enemyLine : styles.badgeLine;
	const badgeClassName = kind === "enemy" ? `${styles.badge} ${styles.enemyBadge}` : styles.badge;

	return (
		<>
			<BaseEdge className={lineClassName} id={id} path={path} />
			{data?.tooltip && (
				<path className={styles.hoverPath} d={path}>
					<title>{data.tooltip}</title>
				</path>
			)}
			<EdgeLabelRenderer>
				<div
					className={badgeClassName}
					style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}>
					<svg aria-hidden="true" viewBox="0 0 24 24">
						{badgeIcon(kind)}
					</svg>
				</div>
			</EdgeLabelRenderer>
		</>
	);
};

export default RelationBadgeEdge;
